import React, { useContext, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import { AppContext } from '../../App';
import { ITrainingSet } from '../../types/my-types';
import { useTrainingSetUpdate } from '../hooks/useTrainingSetUpdate';
import SetVendorsDialog from '../common/Dialog/SetVendorsDialog';
import useGetAllVendors from '../hooks/vendors/useGetAllVendors';
import {
  TypographyPropertiesTitleStyled,
  TypographyPropertiesDataStyled,
} from '../common/StyledComponents';
import { AxiosError } from 'axios';
import { truncateLabel } from '../../utils/trucateLabel';

interface ITrainingSetVendorProps {
  trainingSet: ITrainingSet;
}

const TrainingSetVendor: React.FC<ITrainingSetVendorProps> = ({ trainingSet }) => {
  const { setToast } = useContext(AppContext);
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const queryClient = useQueryClient();
  const { data: allVendors } = useGetAllVendors();

  const { mutateAsync: updateTrainingSet, isPending: isUpdating } = useTrainingSetUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['trainingSet']
      });
      queryClient.invalidateQueries({
        queryKey: ['trainingSets']
      });
      setToast({ variant: 'success', open: true, message: 'Vendors updated.' });
      setOpen(false);
    },
    onError: (error: AxiosError) => {
      setToast({
        variant: 'error',
        open: true,
        message: error.message || 'Failed to update vendors.',
      });
    },
  });

  const handleSubmit = async (vendorIds: number[]) => {
    await updateTrainingSet({
      trainingSetId: trainingSet.id,
      data: { vendors: vendorIds.map((id) => ({ id })) },
    });
  };

  const currentVendorIds = useMemo(
    () => trainingSet.vendors?.map((v) => v.id) ?? [],
    [trainingSet.vendors]
  );

  const vendorNames = useMemo(() => {
    if (!trainingSet.vendors || trainingSet.vendors.length === 0) {
      return [];
    }
    if (!allVendors) {
      return [];
    }
    return trainingSet.vendors
      .map((v) => allVendors.find((av) => av.id === v.id)?.name)
      .filter((name): name is string => !!name);
  }, [trainingSet.vendors, allVendors]);

  const hasVendors = trainingSet.vendors && trainingSet.vendors.length > 0;
  const isLoading = hasVendors && !allVendors;

  return (
    <>
      <div>
        <TypographyPropertiesTitleStyled>VENDOR NAME</TypographyPropertiesTitleStyled>
        <Box
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={() => setOpen(true)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
            gap: '4px',
          }}
          data-cy="training-set-vendor"
        >
          {!hasVendors && <WarningIcon sx={{ color: 'orange', fontSize: 'small' }} />}
          <TypographyPropertiesDataStyled
            data-cy={hasVendors ? 'ts-vendor-name' : 'ts-vendor-not-assigned'}
            sx={{
              color: hasVendors ? undefined : '#999',
              fontStyle: hasVendors ? 'normal' : 'italic',
              display: 'flex',
              flexWrap: 'wrap',
              gap: '2px',
            }}
          >
            {!hasVendors && <span>Not Assigned</span>}
            {isLoading && <span>{trainingSet.vendors?.length} vendor(s)</span>}
            {vendorNames.map((name, index) => {
              const maxLength = vendorNames.length === 1 ? 25 : 13;
              const display = truncateLabel(name, maxLength);
              const isTruncated = name.length > maxLength;
              const isLast = index === vendorNames.length - 1;
              const content = (
                <span key={index}>
                  {display}{!isLast && ','}
                </span>
              );

              if (isTruncated) {
                return (
                  <Tooltip key={index} title={name} placement="top" arrow>
                    {content}
                  </Tooltip>
                );
              }
              return content;
            })}
          </TypographyPropertiesDataStyled>
          <EditIcon
            sx={{
              fontSize: '12px',
              color: '#666',
              opacity: isHovered ? 1 : 0,
              transition: 'opacity 0.2s',
            }}
          />
        </Box>
      </div>

      <SetVendorsDialog
        open={open}
        currentVendorIds={currentVendorIds}
        isUpdating={isUpdating}
        onClose={() => setOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default TrainingSetVendor;
